import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  Archive,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Plus,
  Eye,
  Download,
  File,
  Loader2,
  Search,
  ChevronsUp,
  Edit,
} from 'lucide-react';

import HighlightedText from '@/components/HighlightedText';
import StatusBadge from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipButton } from '@/components/ui/tooltip-button';
import {
  DocumentsContext,
  useDocumentsContext,
} from '@/contexts/DocumentsContext';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { useDocuments } from '@/hooks/useDocuments';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import { usePersistentState } from '@/hooks/usePersistentState';
import { useTemplates } from '@/hooks/useTemplatesAPI';
import PageLayout from '@/layouts/PageLayout';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';
import { startNewRevision } from '@/lib/documentFlow';
import {
  MASTER_LIST_FILTER_GROUPS,
  PENDING_STATUSES,
  cn,
  matchesDocumentSearch,
} from '@/lib/utils';

const PageOptionsPanel = () => {
  const navigate = useNavigate();

  return (
    <Button variant="outline" onClick={() => navigate('/document/new')}>
      <Plus />
      Add Document
    </Button>
  );
};

const DocumentsTableHeader = ({ handleSort, sortBy, sortDirection }) => {
  const { isMasterView } = useDocumentsContext();

  const getSortIcon = (column) => {
    if (sortBy !== column)
      return <ChevronDown className="h-4 w-4 opacity-50" />;

    if (sortDirection === 'asc') return <ChevronUp className="h-4 w-4" />;

    return <ChevronDown className="h-4 w-4" />;
  };

  return (
    <TableHeader>
      <TableRow>
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('serial')}
          >
            Document Number
            {getSortIcon('serial')}
          </Button>
        </TableHead>
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('documentNumber')}
          >
            Document Code
            {getSortIcon('documentNumber')}
          </Button>
        </TableHead>
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('templateId')}
          >
            Doc Type
            {getSortIcon('templateId')}
          </Button>
        </TableHead>
        <TableHead>Rev</TableHead>
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('owner')}
          >
            Doc Owner
            {getSortIcon('owner')}
          </Button>
        </TableHead>
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('name')}
          >
            Doc Title
            {getSortIcon('name')}
          </Button>
        </TableHead>
        {isMasterView && (
          <TableHead>
            <Button
              variant="ghost"
              className="h-auto p-0 font-semibold hover:bg-transparent"
              onClick={() => handleSort('releasedAt')}
            >
              Effective Date
              {getSortIcon('releasedAt')}
            </Button>
          </TableHead>
        )}
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('status')}
          >
            Status
            {getSortIcon('status')}
          </Button>
        </TableHead>
        <TableHead
          className={cn(
            'sticky right-0 z-10',
            'w-48',
            'backdrop-blur bg-background/95 supports-[backdrop-filter]:bg-background/80',
            'text-center',
            'before:absolute before:h-full before:left-0 before:top-0 before:w-px',
            'before:bg-gray-200'
          )}
        >
          Actions
        </TableHead>
      </TableRow>
    </TableHeader>
  );
};

const DocumentsTableBody = () => {
  const navigate = useNavigate();
  const {
    documents,
    isMasterView,
    activeFilter,
    templateTypeMap,
    searchQuery,
  } = useDocumentsContext();
  const { showConfirmModal } = useConfirmModal();
  const { showNotificationModal } = useNotificationModal();

  const handleViewDetail = (documentId, source) => {
    const state = source === 'master' ? { source: 'master' } : undefined;

    navigate(`/document/${documentId}`, { state });
  };

  // Periodic Review: clone the released doc into a DRAFT revision+1, then open
  // that new draft in edit mode so it can be edited and submitted for review.
  const handlePeriodicReview = (doc) =>
    startNewRevision(doc.id, {
      navigate,
      notify: showNotificationModal,
    });

  const handleViewPDF = (documentId) => navigate(`/document/pdf/${documentId}`);

  const handleDownloadPDF = async (documentId) => {
    try {
      const response = await apiService.documents.getPDFUrl(documentId);

      if (response.data.success && response.data.data.pdfUrl)
        window.open(response.data.data.pdfUrl, '_blank');
    } catch (error) {
      console.error('Error downloading PDF:', error);

      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleArchive = async (doc) => {
    showConfirmModal({
      title: 'Archive Document',
      description: `Are you sure you want to archive "${doc.documentTitle}"? This action is irreversible.`,
      onConfirm: async () => {
        try {
          const response = await apiService.documents.archive(doc.id);

          if (response.data.success)
            showNotificationModal(
              'Archived',
              'Document has been archived.',
              'success'
            );
        } catch (error) {
          showNotificationModal(
            'Archive Failed',
            error.response?.data?.error || error.message,
            'error'
          );
        }
      },
    });
  };

  const getDocumentActions = (doc) => {
    const userSub = getUserInfo()?.sub;
    const isCreator = userSub === doc.creator;
    const isOther = !isMasterView && activeFilter === 'other';
    const isApprovedFilter = !isMasterView && activeFilter === 'approved';

    // In "other" filter, only show View Detail
    if (isOther) {
      return [
        {
          icon: <Eye className="h-4 w-4" />,
          tooltip: 'View Detail',
          onClick: () => navigate(`/document/${doc.id}`),
        },
      ];
    }

    const actions = [];

    // Edit — creators can edit drafts and documents returned for revision
    if (
      !isMasterView &&
      ['DRAFT', 'RETURNED'].includes(doc.status) &&
      isCreator
    ) {
      actions.push({
        icon: <Edit className="h-4 w-4" />,
        tooltip: 'Edit',
        onClick: () => navigate(`/document/${doc.id}/edit`),
      });
    }

    // Periodic Review — master list (released documents only)
    if (isMasterView && doc.status === 'RELEASED') {
      actions.push({
        icon: <BookOpen className="h-4 w-4" />,
        tooltip: 'Periodic Review',
        onClick: () => handlePeriodicReview(doc),
      });
    }

    // View Detail
    actions.push({
      icon: <Eye className="h-4 w-4" />,
      tooltip: 'View Detail',
      onClick: () =>
        handleViewDetail(
          doc.id,
          isMasterView ? 'master' : isApprovedFilter ? 'master' : undefined
        ),
    });

    // Release — approved filter in unreleased view
    if (isApprovedFilter && doc.status === 'APPROVED') {
      actions.push({
        icon: <ChevronsUp className="h-4 w-4" />,
        tooltip: 'Release',
        onClick: () =>
          navigate(`/document/${doc.id}/release`, {
            state: {
              title: doc.documentTitle,
              templateId: doc.templateId,
              creator: doc.creator,
            },
          }),
      });
    }

    // View PDF
    actions.push({
      icon: <File className="h-4 w-4" />,
      tooltip: 'View PDF',
      onClick: () => handleViewPDF(doc.id),
    });

    // Download
    actions.push({
      icon: <Download className="h-4 w-4" />,
      tooltip: 'Download',
      onClick: () => handleDownloadPDF(doc.id),
    });

    // Archive — master list, released documents
    if (isMasterView && doc.status === 'RELEASED') {
      actions.push({
        icon: <Archive className="h-4 w-4" />,
        tooltip: 'Archive',
        onClick: () => handleArchive(doc),
        className: 'text-slate-500 hover:text-slate-700',
      });
    }

    return actions;
  };

  return (
    <TableBody>
      {documents.map((document) => {
        return (
          <TableRow key={document.id}>
            <TableCell>
              <HighlightedText
                text={document.serial || '-'}
                query={searchQuery}
              />
            </TableCell>
            <TableCell>
              <HighlightedText
                text={document.documentNumber || '-'}
                query={searchQuery}
              />
            </TableCell>
            <TableCell>
              <Badge variant="outline">
                <HighlightedText
                  text={
                    templateTypeMap[document.templateId] || document.templateId
                  }
                  query={searchQuery}
                />
              </Badge>
            </TableCell>
            <TableCell>
              <HighlightedText
                text={document.revision ?? '-'}
                query={searchQuery}
              />
            </TableCell>
            <TableCell>
              <HighlightedText
                text={
                  document.owner || document.creatorName || document.creator
                }
                query={searchQuery}
              />
            </TableCell>
            <TableCell>
              <HighlightedText
                text={document.documentTitle}
                query={searchQuery}
              />
            </TableCell>
            {isMasterView && (
              <TableCell>
                <HighlightedText
                  text={document.releasedAt?.split('T')[0] || '-'}
                  query={searchQuery}
                />
              </TableCell>
            )}
            <TableCell>
              <StatusBadge status={document.status} query={searchQuery} />
            </TableCell>
            <TableCell
              className={cn(
                'sticky right-0 z-10',
                'space-x-2 whitespace-nowrap',
                'backdrop-blur bg-background/95 supports-[backdrop-filter]:bg-background/80',
                'after:absolute after:h-px after:left-0 after:-top-px after:w-full',
                'before:absolute before:h-full before:left-0 before:top-0 before:w-px',
                'after:bg-gray-200 before:bg-gray-200'
              )}
            >
              {getDocumentActions(document).map((action) => (
                <TooltipButton
                  key={action.tooltip}
                  onClick={action.onClick}
                  tooltip={action.tooltip}
                  variant="outline"
                  className={action.className}
                >
                  {action.icon}
                </TooltipButton>
              ))}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
};

const DocumentListPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state || 'master');
  const [activeFilter, handleFilter] = useState('attention');
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const navigate = useNavigate();
  const { documents, loading } = useDocuments();
  const { templates, loading: templatesLoading } = useTemplates({});
  const [searchQuery, setSearchQuery] = useState('');

  const templateTypeMap = Object.fromEntries(
    templates.map((t) => [t.id, t.type])
  );

  // Master list visibility toggles (default: both included).
  // Persisted so navigating away and back doesn't reset them.
  const [includeArchived, setIncludeArchived] = usePersistentState(
    'documents.includeArchived',
    true
  );
  const [includeRejected, setIncludeRejected] = usePersistentState(
    'documents.includeRejected',
    false
  );

  const isMasterView = activeTab === 'master';

  const applySearch = (docList) =>
    docList.filter((doc) =>
      matchesDocumentSearch(doc, searchQuery, templateTypeMap)
    );

  const applySort = (list, sortBy, sortDirection) => {
    if (!sortBy) return list;

    return [...list].sort((a, b) => {
      const aVal = a[sortBy] ?? '';
      const bVal = b[sortBy] ?? '';
      const result = String(aVal).localeCompare(String(bVal));

      return sortDirection === 'asc' ? result : -result;
    });
  };

  const handleSort = (column) => {
    if (sortBy === column)
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const userSub = getUserInfo()?.sub;
  const userGroups = getUserInfo()?.['cognito:groups'] || [];
  const canFilter = userGroups.some((role) =>
    MASTER_LIST_FILTER_GROUPS.includes(role)
  );

  const applyStatusFilter = (docList) => {
    if (isMasterView)
      return docList.filter((d) => {
        if (d.status === 'RELEASED') return true;

        if (d.status === 'ARCHIVED') return canFilter && includeArchived;

        if (d.status === 'REJECTED') return canFilter && includeRejected;

        return false;
      });

    switch (activeFilter) {
      case 'attention': {
        const getSub = (r) => (typeof r === 'object' ? r?.sub : r);

        return docList.filter((d) => {
          if (d.status === 'REVIEWING')
            return (
              getSub(d.reviewerPipeline?.[d.currentActorIndex]) === userSub
            );

          if (d.status === 'RETURNED') return d.creator === userSub;

          if (d.status === 'DRAFT') return d.creator === userSub;
        });
      }

      case 'approved':
        return docList.filter((d) => d.status === 'APPROVED');

      case 'other': {
        // Not pending => not this filter. The admin bypass below widens *whose*
        // pending documents are visible, not *which* statuses count as pending.
        const getSub = (r) => (typeof r === 'object' ? r?.sub : r);

        return docList.filter((d) => {
          if (!PENDING_STATUSES.includes(d.status)) return false;

          // Drafts are private to their creator ("Your Attention Needed").
          if (d.status === 'DRAFT') return false;

          // Admins can see all pending docs, but READ ONLY
          if (
            userGroups.some((role) =>
              ['PowerUser', 'SuperAdmin'].includes(role)
            )
          )
            return true;

          if (d.status === 'REVIEWING')
            return (
              getSub(d.reviewerPipeline?.[d.currentActorIndex]) !== userSub
            );

          if (d.status === 'RETURNED') return d.creator !== userSub;

          return false;
        });
      }

      default:
        return docList;
    }
  };

  const filteredDocuments = applySort(
    applyStatusFilter(applySearch(documents)),
    sortBy,
    sortDirection
  );

  const handleTab = (value) => {
    navigate('.', { replace: true, state: value });
    setActiveTab(value);
    setSearchQuery('');
  };

  return (
    <DocumentsContext.Provider
      value={{
        documents: filteredDocuments,
        isMasterView,
        activeFilter,
        templateTypeMap,
        searchQuery,
      }}
    >
      <PageLayout
        title={isMasterView ? 'Document Master List' : 'Unreleased Documents'}
        toolbarTail={<PageOptionsPanel />}
      >
        <Tabs
          className="[&_[role=tablist]]:mb-0"
          onValueChange={handleTab}
          value={activeTab}
        >
          <TabsList
            className={cn(
              'w-full flex mb-6 gap-x-2 gap-y-1 flex-wrap',
              !isMasterView && 'rounded-b-none'
            )}
          >
            <TabsTrigger value="master">Document Master List</TabsTrigger>
            <TabsTrigger value="pending">Unreleased Documents</TabsTrigger>
          </TabsList>
        </Tabs>
        {!isMasterView && (
          <Tabs onValueChange={handleFilter} value={activeFilter}>
            <TabsList
              className={cn(
                'w-full flex gap-x-2 gap-y-1 flex-wrap',
                'bg-gray-50 rounded-t-none'
              )}
            >
              <TabsTrigger value="attention">Your Attention Needed</TabsTrigger>
              <TabsTrigger value="approved">Approved Documents</TabsTrigger>
              <TabsTrigger value="other">Other Documents</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <div className="flex gap-4 items-center mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-10 text-lg font-semibold"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search across all fields..."
          />
          {isMasterView && canFilter && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto shrink-0"
                >
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Include</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={includeArchived}
                  onCheckedChange={setIncludeArchived}
                >
                  Archived Documents
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={includeRejected}
                  onCheckedChange={setIncludeRejected}
                >
                  Rejected Documents
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="mt-4 rounded-md border">
          {loading || templatesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">Loading templates...</span>
            </div>
          ) : filteredDocuments.length ? (
            <Table>
              <DocumentsTableHeader
                handleSort={handleSort}
                sortBy={sortBy}
                sortDirection={sortDirection}
              />
              <DocumentsTableBody />
            </Table>
          ) : (
            <div className="py-8 text-center text-gray-400">No Document</div>
          )}
        </div>
      </PageLayout>
    </DocumentsContext.Provider>
  );
};

export default DocumentListPage;
